import { Currency } from '../../types/PaymentTypes';

export const formatAmount = ({
  amount = 0,
  currency,
  currencyCode,
  format = CurrencyFormat.SYMBOL,
  fractionDigits = 0,
  locale,
  minimumIntegerDigits,
}: {
  amount?: number;
  currency?: Currency;
  currencyCode?: string;
  format?: CurrencyFormat;
  fractionDigits?: number;
  locale?: string;
  minimumIntegerDigits?: number;
}) => {
  // CAUTION: If you want to use other currencies and locales (especially the "both"-option),
  // make sure to check that the result since locales can differ a lot
  const currencyCodeToUse = currencyCode ?? currency?.code;
  return `${new Intl.NumberFormat(locale, {
    style: currencyCode && format !== CurrencyFormat.CODE ? 'currency' : undefined,
    currency: currencyCodeToUse,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    minimumIntegerDigits,
  }).format(amount)}${format !== CurrencyFormat.SYMBOL ? ` ${currencyCodeToUse}` : ''}`;
};

function getLocaleFromCurrencyCode(currencyCode: string): string {
  switch (currencyCode) {
    case 'SEK':
      return 'sv-SE';
    case 'AUD':
      return 'en-AU';
    case 'DKK':
      return 'da-DE';
    case 'NOK':
      return 'nn-NO';

    default:
      return 'en-US';
  }
}

export enum CurrencyFormat {
  SYMBOL = 'symbol',
  CODE = 'code',
  SYMBOL_AND_CODE = 'symbolAndCode',
}

export function getCurrencySymbol(currencyCode: string): string {
  const locale = getLocaleFromCurrencyCode(currencyCode);
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  });

  // NOTE: 3500 is a nonsense number to get the currency part.
  // The currency string, such as the symbols "$" and "€" or the name "Dollar", "Euro" depending on how currencyDisplay is specified.
  const result = formatter.formatToParts(3500).find(({ type }) => type === 'currency');
  return result?.value ?? currencyCode;
}

export function formatAmountAndWithDerivedLocale(amount: number, currencyCode: string): string {
  const locale = getLocaleFromCurrencyCode(currencyCode);

  // Note, this requires node v. 14+ for tests to work
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    currencyDisplay: 'symbol', // narrowSymbol not supported in Safari older versions, see https://caniuse.com/?search=currencyDisplay
  }).format(amount);
}
